import { StaticImage } from "gatsby-plugin-image"
import cc from "classcat"
import LocalizedLink from "components/LocalizedLink"
import { useContext } from "react"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"

type Report2025ReferenceSectionProps = { theme?: "light" | "dark" }

export default function Report2025ReferenceSection({
  theme = "light",
}: Report2025ReferenceSectionProps) {
  const isDark = theme === "dark"
  const { locale } = useContext(LocaleContext)
  const {
    report2025HomepageSectionTitle,
    report2025HomepageSectionDescription,
    report2025HomepageSectionCta,
  } = useTranslations()

  return (
    <section
      className={cc([
        "relative py-16 lg:py-24",
        isDark ? "bg-zinc-800" : "bg-white",
      ])}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-12 lg:px-8">
        <div className="order-2">
          <h2
            className={cc([
              "whitespace-pre-line text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl",
              isDark ? "text-zinc-100" : "text-zinc-900",
            ])}
          >
            {report2025HomepageSectionTitle}
          </h2>
          <p
            className={cc([
              "mt-6 text-lg leading-relaxed",
              isDark ? "text-zinc-200" : "text-zinc-700",
            ])}
          >
            {report2025HomepageSectionDescription}
          </p>
          <div className="mt-8">
            <LocalizedLink
              to="/schools/annual-reports/"
              className={cc([
                "inline-flex items-center rounded-xl bg-kkm-green px-6 py-3 text-base font-semibold text-zinc-900 transition hover:bg-kkm-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2",
                isDark
                  ? "focus:ring-zinc-100/40 focus:ring-offset-zinc-800"
                  : "focus:ring-zinc-900/30 focus:ring-offset-white",
              ])}
            >
              {report2025HomepageSectionCta}
            </LocalizedLink>
          </div>
        </div>
        <div
          className={cc([
            "order-1 overflow-hidden rounded-2xl ring-1",
            isDark ? "ring-zinc-700" : "ring-zinc-200",
          ])}
        >
          {locale.startsWith("sv") ? (
            <StaticImage
              src="../../../images/annual-reports/blog-sv.jpg"
              alt=""
              placeholder="blurred"
              className="!h-full !w-full"
              imgClassName="h-full w-full object-cover"
            />
          ) : null}
          {locale.startsWith("en") ? (
            <StaticImage
              src="../../../images/annual-reports/blog-en.jpg"
              alt=""
              placeholder="blurred"
              className="!h-full !w-full"
              imgClassName="h-full w-full object-cover"
            />
          ) : null}
          {!locale.startsWith("sv") && !locale.startsWith("en") ? (
            <StaticImage
              src="../../../images/annual-reports/blog-fi.jpg"
              alt=""
              placeholder="blurred"
              className="!h-full !w-full"
              imgClassName="h-full w-full object-cover"
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}
